import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to keep the screen awake
 * Uses the standard Native Wake Lock API
 */
export function useWakeLock() {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [isActive, setIsActive] = useState(false);
  const hasWakeLockAPI = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  // Request wake lock (enable)
  const enable = useCallback(async () => {
    if (!hasWakeLockAPI) {
      console.warn('此瀏覽器不支援 Wake Lock，請手動調整螢幕常亮設定');
      return false;
    }

    try {
      // Check if we already have a lock to avoid redundant requests
      if (wakeLockRef.current && !wakeLockRef.current.released) {
          return true;
      }

      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      setIsActive(true);
      console.log('Wake Lock acquired');
      
      lock.addEventListener('release', () => {
         // Only clear if this is the current lock
         if (wakeLockRef.current === lock) {
             wakeLockRef.current = null;
         }
         setIsActive(false);
         console.log('Wake Lock released');
      });
      
      return true;
    } catch (err) {
      console.error('無法啟用螢幕常亮:', err);
      return false;
    }
  }, [hasWakeLockAPI]);

  // Release wake lock (disable)
  const disable = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (err) {
        console.error('Failed to release Wake Lock:', err);
      }
    }
    setIsActive(false);
  }, []);

  // Handle visibility change to auto-reacquire
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current && isActive) {
        await enable();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cleanup on unmount handled by a separate effect loop if needed, 
      // but typically we want to keep it if the component is mounted.
      // However, if the component unmounts, we should release.
    };
  }, [isActive, enable]);

  // Cleanup on unmount
  useEffect(() => {
      return () => {
          disable();
      };
  }, [disable]);

  return {
    isSupported: hasWakeLockAPI,
    isActive,
    enable,
    disable,
    requestWakeLock: enable, // Alias for backward compatibility if needed, or update consumers
    releaseWakeLock: disable // Alias
  };
}

